import React, { useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import Help from './components/Help';
import FolderBrowser from './components/navigation/FolderBrowser';

import { Backdrop, CircularProgress } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import 'core-js';
import 'normalize.css';
import FindReplace from './FindReplace/FindReplace';
import ExperienceSwitcher from './components/ExperienceSwitcher';
import VideoPlayerApp from './components/subtitles/SubtitleEditor';
import Login from './components/users/Login';
import { LoggingProvider } from './folders/LoggingProvider';
import './libs/contextmenu.css';
import fileSystemAccessService from './services/FileSystemAccessService';
// Import admin components
import AdminDashboard from './components/admin/AdminDashboard';
import DataMigration from './components/admin/DataMigration';
import MongoFilesBrowser from './components/admin/MongoFilesBrowser';
import MongoVideosBrowser from './components/admin/MongoVideosBrowser';
import UserManagement from './components/admin/UserManagement';
import BugReportsAdmin from './components/admin/BugReportsAdmin';
import AssignmentDashboard from './components/admin/AssignmentDashboard';
import ProcessingStatusDashboard from './components/status/ProcessingStatusDashboard';
import { AssignmentProvider } from './contexts/AssignmentContext';
// Import video processing components
import { VideoProcessingProvider } from './contexts/VideoProcessingContext';
import VideoProcessingPanel from './components/status/VideoProcessingPanel';
// Import new components
import VideoEditingHelpPage from './components/help/VideoEditingHelpPage';
import BugReportPage from './components/bugreport/BugReportPage';
import SystemStatusPage from './components/status/SystemStatusPage';
import ActiveSessionsPage from './components/status/ActiveSessionsPage';
import AppThemeProvider from './components/ThemeProvider';
import MultiColumnSubtitleViewer from './components/subtitles/multi-column/MultiColumnSubtitleViewer';
import TrashBinPage from './components/trash/TrashBinPage';
import EditingStatsPage from './components/editingstats/EditingStatsPage';

const defaultLang = 'en';

const AppContent = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const [authToken, setAuthToken] = useState(null);
    const [canNavigateToEdit, setCanNavigateToEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [videoExperience, setVideoExperience] = useState(fileSystemAccessService.getVideoExperience());
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        const authToken = localStorage.getItem('authToken');
        const authUser = JSON.parse(localStorage.getItem('authUser'));

        console.log('Auth Token:', authToken);
        console.log('Auth User:', authUser);
        console.log('Is admin:', authUser?.userType === 'admin');
        
        setAuthToken(authToken);
        // Check if user is admin based on stored user data
        setIsAdmin(authUser?.userType === 'admin');

        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 2000);

        return () => {
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        const handleStorageChange = () => {
            setVideoExperience(fileSystemAccessService.getVideoExperience());
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('videoExperienceChanged', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('videoExperienceChanged', handleStorageChange);
        };
    }, []);

    // Admin route guard component
    const AdminRoute = ({ children }) => {
        return isAdmin ? children : <Navigate to="/" />;
    };

    // Protected route component for non-admin users
    const ProtectedRoute = ({ children }) => {
        return authToken !== null ? children : <Navigate to="/login" />;
    };

    // Pass theme props to components that need them
    const renderComponent = (Component, props = {}) => {
        return <Component 
            {...props} 
            darkMode={darkMode} 
            toggleDarkMode={toggleDarkMode} 
            themeVariant={themeVariant} 
        />;
    };

    if (isLoading) {
        return (
            <AssignmentProvider>
                <LoggingProvider>
                    <VideoProcessingProvider>
                        <AppBar position="static" style={{ 
                            backgroundColor: darkMode ? '#1E1E2D' : '#2C3D50'
                        }}>
                            <Container maxWidth="xl">
                                <Toolbar disableGutters>
                                    <Typography
                                        variant="h6"
                                        noWrap
                                        component={'a'}
                                        target="_blank"
                                        href="https://www.srmd.org/"
                                        sx={{
                                            mr: 2,
                                            display: { xs: 'none', md: 'flex' },
                                            fontFamily: 'monospace',
                                            fontWeight: 700,
                                            letterSpacing: '.3rem',
                                            color: 'inherit',
                                            textDecoration: 'none',
                                        }}
                                    >
                                        <img
                                            style={{ width: '70px', height: '60px' }}
                                            className="SRMD-img"
                                            src={'/SRMD-Logo.png'}
                                            alt="SRMD_Logo"
                                        />
                                    </Typography>
                                </Toolbar>
                            </Container>
                        </AppBar>
                        <Backdrop sx={{ color: '#FFF', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
                            <CircularProgress color="inherit" />
                        </Backdrop>
                    </VideoProcessingProvider>
                </LoggingProvider>
            </AssignmentProvider>
        );
    } else {
        return (
            <AssignmentProvider>
                <LoggingProvider>
                    <VideoProcessingProvider>
                        <Router>
                            {authToken === null ? (
                                <>
                                    
                                    <Routes>
                                        <Route path="/login" element={renderComponent(Login)} />
                                        <Route path="*" element={<Navigate to="/login" />} />
                                    </Routes>
                                </>
                            ) : (
                                <>
                                    <Routes>
                                        <Route path="/" element={renderComponent(FolderBrowser, { setCanNavigateToEdit })} />
                                        <Route
                                            path="/videos/edit"
                                            element={
                                                canNavigateToEdit ? (
                                                    <VideoPlayerApp defaultLang={defaultLang} />
                                                ) : (
                                                    <Navigate to="/" />
                                                )
                                            }
                                        />
                                        <Route exact path="/find-replace" element={renderComponent(FindReplace)} />
                                        <Route path="/help" element={renderComponent(VideoEditingHelpPage)} />
                                        <Route path="/bug-report" element={renderComponent(BugReportPage)} />
                                        <Route path="/system-status" element={renderComponent(SystemStatusPage)} />
                                        <Route path="/status" element={<Navigate to="/system-status" replace />} />
                                        <Route path="/sessions/active" element={renderComponent(ActiveSessionsPage)} />
                                        <Route path="/status/processing-status" element={renderComponent(ProcessingStatusDashboard)} />
                                        <Route path="/multi-column-viewer/:videoId" element={renderComponent(MultiColumnSubtitleViewer)} />
                                        <Route path="/editing-stats" element={renderComponent(EditingStatsPage)} />
                                        {/* Admin Routes */}
                                        <Route 
                                            path="/admin" 
                                            element={
                                                <AdminRoute>
                                                    {renderComponent(AdminDashboard)}
                                                </AdminRoute>
                                            } 
                                        />
                                        <Route 
                                            path="/admin/users" 
                                            element={
                                                <AdminRoute>
                                                    {renderComponent(UserManagement)}
                                                </AdminRoute>
                                            } 
                                        />
                                        <Route 
                                            path="/admin/migration" 
                                            element={
                                                <AdminRoute>
                                                    {renderComponent(DataMigration)}
                                                </AdminRoute>
                                            } 
                                        />
                                        <Route path="/admin/mongo-files" element={renderComponent(MongoFilesBrowser)} />
                                        <Route path="/admin/mongo-videos" element={renderComponent(MongoVideosBrowser)} />
                                        <Route 
                                            path="/admin/bug-reports" 
                                            element={
                                                <AdminRoute>
                                                    {renderComponent(BugReportsAdmin)}
                                                </AdminRoute>
                                            } 
                                        />
                                        <Route 
                                            path="/admin/assignments" 
                                            element={
                                                <AdminRoute>
                                                    {renderComponent(AssignmentDashboard)}
                                                </AdminRoute>
                                            } 
                                        />
                                        <Route
                                            path="/trash"
                                            element={
                                                <AdminRoute>
                                                    {renderComponent(TrashBinPage)}
                                                </AdminRoute>
                                            }
                                        />
                                        
                                        <Route path="*" element={<Navigate to="/" />} />
                                    </Routes>
                                </>
                            )}
                        </Router>
                        <VideoProcessingPanel />
                    </VideoProcessingProvider>
                </LoggingProvider>
            </AssignmentProvider>
        );
    }
};

const App = () => {
    return (
        <AppThemeProvider>
            <AppContent />
        </AppThemeProvider>
    );
};

export default App;
